import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@igo2/core/language';
import { MessageService } from '@igo2/core/message';
import { addLayerAndFeaturesStyledToMap, addLayerAndFeaturesToMap, ArcGISRestDataSource, ArcGISRestDataSourceOptions, buildUrl, computeOlFeaturesExtent, DataSourceOptions, DataSourceService, Feature, FeatureDataSource, FeatureDataSourceOptions, featureRandomStyle, featureToOl, formatWFSQueryString, IgoMap, ImageArcGISRestDataSource, isLayerItem, Layer, LayerService, moveToOlFeatures, QueryableDataSourceOptions, SourceFieldsOptionsParams, StyleService, VectorLayer, WFSDataSourceOptions, WMSDataSource } from '@igo2/geo';
import { MapState, WorkspaceState } from '@igo2/integration';
import { Feature as olFeature} from 'ol';
import olSourceVector, { VectorSourceEvent } from 'ol/source/Vector';
import { createOrUpdateFromRings, Extent } from 'ol/extent';
import * as olproj from 'ol/proj';
import { get as getProjection, Projection as olProjection } from 'ol/proj';
import { Circle, Geometry, Point, Polygon, SimpleGeometry } from 'ol/geom';
import { circular, fromCircle } from 'ol/geom/Polygon';
import {GeoJSON} from 'ol/format';
import olFormatEsriJSON from 'ol/format/EsriJSON';
import { AuthInterceptor } from '@igo2/auth';
import VectorSource from 'ol/source/Vector';
import * as turf from '@turf/turf';
import { uuid } from '@igo2/utils';
import { format } from 'path';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import JSONFeature from 'ol/format/JSONFeature';
import { TRUE } from 'ol/functions';
import GML3 from 'ol/format/GML3';

const defaultMaxFeatures = 5000;
interface ArcGISQueryResponse {
  features: EsriFeature[];
  fields: EsriField[];
  numberReturned?: number;
  totalFeatures?: number;
  exceededTransferLimit?: boolean;
  error?: ArcGISError;
  count?: number;
}

interface ArcGISError {
  code: number;
  message: string;
  details: string[];
  infos: any[];
}

interface EsriField {
  name: string;
  alias: string;
  type: string;
}

interface EsriFeature {
  attributes: { [key: string]: any };
  geometry?: EsriGeometry;
}

interface EsriGeometry {
  hasZ?:boolean;
  hasM?:boolean;
  points?:any[];
  paths?:any[];
  rings?:any[];
  ids?:any[];
  id?:number;
  x?: number;
  y?: number;
  xmin?: number;
  ymin?: number;
  xmax?: number;
  ymax?: number;
  spatialReference?: EsriSpatialReference;
}

interface EsriSpatialReference {
  wkid?: number;
  latestWkid?: number;
}

@Injectable({
  providedIn: 'root'
})

export class SigtExtractByPolygonService {
  #mapState = inject(MapState);
  #dataSourceService = inject(DataSourceService);
  #layerService = inject(LayerService);
  languageService = inject(LanguageService);
  workspaceState = inject(WorkspaceState);
  messageService = inject(MessageService);
  styleService = inject(StyleService);

  constructor(
    public authInterceptor?: AuthInterceptor

  ) { }

   doExtract(sourceLayer:Layer, selectionLayer:VectorLayer) {
    const selectionFeatureCount = selectionLayer.dataSource.ol.getFeatures().length;
    if(selectionFeatureCount !== 1) {
      this.messageService.error(
        'sigt.integration.extractByPolygon.service.onlyOnePolygon', 
        'sigt.integration.extractByPolygon.service.genericMessageTitle',
        undefined,
        {selectionFeatureCount:selectionFeatureCount}
        );
      return;
    }
    let selectionFeature:olFeature;
    const origSelectionFeature = selectionLayer.dataSource.ol.getFeatures()[0];
    //handle circle drawing selection
    if (origSelectionFeature.getGeometry().getType() === "Point" && origSelectionFeature.get("rad") !== undefined) {
      const radius = origSelectionFeature.get('rad');
      if (radius) {
          const center4326 = [
              origSelectionFeature.get('longitude'),
              origSelectionFeature.get('latitude')
          ];
          const circle = circular(center4326, radius, 500);
          circle.transform('EPSG:4326', origSelectionFeature.get('_projection'));
          selectionFeature= new olFeature(circle);         
      } else {
        this.messageService.error(
        'sigt.integration.extractByPolygon.service.invalidGeometry', 
        'sigt.integration.extractByPolygon.service.genericMessageTitle',
        );
      }
    } else {
      selectionFeature = origSelectionFeature;
    }

    const bbox:Extent = selectionFeature.getGeometry().getExtent();
    console.debug("bbox:",bbox[0], bbox[1], bbox[2], bbox[3]);
    
    const datasource = sourceLayer.dataSource;
    if (datasource instanceof WMSDataSource && datasource.options.urlWfs !== undefined) {   
      this.getWFSFeatures(datasource, bbox, selectionFeature)
        .then(features => {
          console.debug('Fetched features:', features.length);
          if (features) {
            const layertitle = `${sourceLayer.title} - Extraction`;
            this.createIgoLayer(layertitle, features, datasource.options.sourceFields);
            this.messageService.success(
              'sigt.integration.extractByPolygon.service.layerCreated',
              'sigt.integration.extractByPolygon.service.genericMessageTitle',
              undefined,
              {title:layertitle, count:features.length}
            );            
          }
        })
        .catch(error => {
          console.error('Error fetching features:', error);
        });
    } else if (datasource instanceof WMSDataSource && (<any> datasource.options).urlEsri !== undefined) {
      this.getArcgisRestFeatures(datasource.options, selectionFeature)
        .then(features => {
          console.debug('Fetched features:', features.length);
          if (features) {
            const layertitle = `${sourceLayer.title} - Extraction`;
            this.createIgoLayer(layertitle, features, datasource.options.sourceFields);
            this.messageService.success(
              'sigt.integration.extractByPolygon.service.layerCreated',
              'sigt.integration.extractByPolygon.service.genericMessageTitle',
              undefined,
              {title:layertitle, count:features.length}
            );            
          }
        })
        .catch(error => {
          console.error('Error fetching features:', error);
        });
    } else if (datasource instanceof ImageArcGISRestDataSource && (<any> datasource.options).urlEsri !== undefined) {
      this.getArcgisRestFeatures(datasource.options, selectionFeature)
        .then(features => {
          console.debug('Fetched features:', features.length);
          if (features) {
            const layertitle = `${sourceLayer.title} - Extraction`;
            this.createIgoLayer(layertitle, features, datasource.options.sourceFields);
            this.messageService.success(
              'sigt.integration.extractByPolygon.service.layerCreated',
              'sigt.integration.extractByPolygon.service.genericMessageTitle',
              undefined,
              {title:layertitle, count:features.length}
            );
          }
        })
        .catch(error => {
          console.error('Error fetching features:', error);
        });        
    } else {
        this.messageService.error(
          'sigt.integration.extractByPolygon.service.unsupportedLayerType',
          'sigt.integration.extractByPolygon.service.genericMessageTitle',
        );
    }
  }

async getWFSFeatures(
  datasource: WMSDataSource,
  bbox: Extent, 
  selectionFeature:olFeature<Geometry>
): Promise<olFeature<Geometry>[]> {
    const projection = getProjection(this.#mapState.map.projectionCode);
    const wfsDataSourceOptions:WFSDataSourceOptions = {
      params: datasource.options.paramsWFS,
      paramsWFS: datasource.options.paramsWFS,
      urlWfs: datasource.options.urlWfs,
      sourceFields: datasource.options.sourceFields
    };
    let sourceVectorOptions:any;

    if (wfsDataSourceOptions.paramsWFS.version === "2.0.0") {
      const supportPaging = await this.checkSupportForPagedResults(wfsDataSourceOptions, projection);
      //Force the retrieval by chunks on version 2.0.0 if supported
      if (supportPaging) {
         wfsDataSourceOptions.paramsWFS.maxFeatures = defaultMaxFeatures+1;
      }
      sourceVectorOptions = {format: new GeoJSON()};
    } else {
      sourceVectorOptions = {format: new GML3()};
    }

    const selectionFeatures = [selectionFeature];
    const vectorSource = new olSourceVector(sourceVectorOptions);
    return new Promise<olFeature<Geometry>[]>((resolve, reject) => {  
      this.fetchFeaturesByChunks(
        vectorSource, 
        wfsDataSourceOptions, 
        bbox,
        selectionFeatures,
        projection, 
        (features)=>{
        resolve(features);
        }, 
        ()=>{
        this.messageService.error(
          'sigt.integration.extractByPolygon.service.failedWFSExtract',
          'sigt.integration.extractByPolygon.service.genericMessageTitle',
        );
        reject(new Error('Failed to fetch WFS features'));;
      });
    });
  }

  async checkSupportForPagedResults(
    wfsDataSourceOptions: WFSDataSourceOptions,
    projection: olproj.Projection
  ): Promise<boolean> {
    const wfsQueryStrings = formatWFSQueryString(
      wfsDataSourceOptions,
      1, // count
      projection.getCode(),
      undefined,
      0, // startIndex
      false
    );

    const baseUrl = wfsQueryStrings.find(f => f.name === 'getfeature')!.value;
    const alteredUrlWithKeyAuth = this.authInterceptor.alterUrlWithKeyAuth(baseUrl);
    const modifiedUrl = alteredUrlWithKeyAuth || baseUrl;

    return new Promise<boolean>((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', modifiedUrl);

      if (this.authInterceptor) {
        this.authInterceptor.interceptXhr(xhr, modifiedUrl);
      }

      xhr.onload = () => {
        if (xhr.status === 200) {
          const responseText = xhr.responseText;
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(responseText, 'application/xml');
          const exceptionText = xmlDoc.querySelector('ows\\:ExceptionText')?.textContent;

          if (exceptionText 
              //&& exceptionText.includes('Cannot do natural order without a primary key')
            ) {
            resolve(false);
          } else {
            resolve(true);
          }
        } else {
          resolve(false);
        }
      };

      xhr.onerror = () => resolve(false);
      xhr.send();
    });
  }

  /**
   * Projection OpenLayers enregistrée pour un code, par défaut celle de la carte.
   * new Projection({ code }) crée un objet que transformExtent ne sait relier à
   * aucune transformation (« transformFn is not a function ») ; get() reconnaît
   * les codes enregistrés, y compris sous forme URN (urn:ogc:def:crs:EPSG::3857,
   * forme employée par toutes les couches WFS des contextes).
   */
  private resoudreProjection(code: string | undefined, defaut: olProjection): olProjection {
    if (!code) {
      return defaut;
    }
    return getProjection(code) ?? new olProjection({ code });
  }

  async fetchFeaturesByChunks(
    vectorSource: olSourceVector,
    options: WFSDataSourceOptions,
    extent: Extent,
    selectionFeatures: olFeature[],
    proj: olProjection,
    success: (features: olFeature[]) => void,
    failure: () => void,
    randomParam?: boolean
  ) {
    {
      const paramsWFS = options.paramsWFS;
      const wfsProj = this.resoudreProjection(paramsWFS.srsName, proj);
      const currentExtent = olproj.transformExtent(extent, proj, wfsProj);
      paramsWFS.srsName = paramsWFS.srsName || proj.getCode();

      const baseUrl = buildUrl(
        options,
        currentExtent,
        wfsProj,
        randomParam
      );

      if (
        paramsWFS.version === '2.0.0' &&
        paramsWFS.maxFeatures > defaultMaxFeatures
      ) {
          const chunkSize = 1000;
          let startIndex = 0;
          let allFeatures: olFeature[] = [];
          let totalFeatures = Infinity;

          try {
            while (startIndex < totalFeatures) {
              let url = baseUrl
                .replace(`count=${paramsWFS.maxFeatures}`, `count=${chunkSize}`)
                .replace(/startIndex=\d+/, '') + `&startIndex=${startIndex}`;

              const { features, numberMatched } = await this.fetchFeaturesChunk(
                url,
                currentExtent,
                vectorSource,
                proj
              );
              let filteredFeatures = features;
              if (selectionFeatures.length === 1){
                filteredFeatures = this.filterFeatures(features, selectionFeatures);
              }
              allFeatures = allFeatures.concat(filteredFeatures);

              if (totalFeatures === Infinity) {
                totalFeatures = numberMatched;
              }

              startIndex += chunkSize;

              if (features.length === 0 || allFeatures.length >= totalFeatures) {
                break;
              }
            }

            success(allFeatures);
          } catch (error) {
            failure();
          }
      } else {
        try {
          const allFeaturesUrl = baseUrl.replace(/&startIndex=\d+/, '').replace(/&count=\d+/,'');
          const { features, numberMatched } =  await this.getFeatures(
            vectorSource,
            currentExtent,
            wfsProj,
            proj,
            allFeaturesUrl
          );
          if (features.length !== numberMatched) {
            this.messageService.alert(
              'sigt.integration.extractByPolygon.service.incompleteResults',
              'sigt.integration.extractByPolygon.service.genericMessageTitle',
            );
          }
          let filteredFeatures = features;
          if (selectionFeatures.length === 1){
            filteredFeatures = this.filterFeatures(features, selectionFeatures);
          }
          success(filteredFeatures);
        } catch (error) {
            failure();
        }
      }
    }

  }

  private async fetchFeaturesChunk(
    url: string,
    extent: Extent,
    vectorSource: olSourceVector,
    featureProjection: olProjection
  ): Promise<{ features: olFeature[]; numberMatched: number }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const alteredUrlWithKeyAuth = this.authInterceptor.alterUrlWithKeyAuth(url);
      let modifiedUrl = url;
      if (alteredUrlWithKeyAuth) {
        modifiedUrl = alteredUrlWithKeyAuth;
      }

      xhr.open('GET', modifiedUrl);
      if (this.authInterceptor) {
        this.authInterceptor.interceptXhr(xhr, modifiedUrl);
      }

      xhr.onload = () => {
        if (xhr.status === 200 && xhr.responseText.length > 0) {
          const format = vectorSource.getFormat();
          const type = format.getType();

          let source: string | object | Document | Element | ArrayBuffer;
          let numberMatched = 0;

          try {
            if (type === 'xml') {
              const xml = new DOMParser().parseFromString(xhr.responseText, 'application/xml');
              source = xml;
              numberMatched = parseInt(xml.documentElement.getAttribute('numberMatched') || '0', 10);
            } else if (type === 'json') {
              const json = JSON.parse(xhr.responseText);
              source = json;
              numberMatched = json.numberMatched || json.totalFeatures || 0;
            } else {
              source = xhr.responseText;
            }
            if (source) {
              const features = format.readFeatures(source, {
                extent,
                featureProjection
              }) as olFeature[];

              vectorSource.addFeatures(features);
              resolve({ features, numberMatched });
            }
          } catch (err) {
            reject(err);
          }
        } else {
          reject();
        }
      };

      xhr.onerror = () => reject();
      xhr.send();
    });
  }

  /**
   *  get feature from a WFS datasource
   * @internal
   * @param vectorSource the vector source to be created
   * @param extent the extent of the requested data
   * @param dataProjection the projection of the retrieved data
   * @param featureProjection the projection of the created features
   * @param url the url string to retrieve the data
   */
  private async getFeatures (
    vectorSource: olSourceVector,
    extent: Extent,
    dataProjection: olProjection,
    featureProjection: olProjection,
    url: string
  ): Promise<{ features: olFeature[]; numberMatched: number }> {
   return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const alteredUrlWithKeyAuth = this.authInterceptor.alterUrlWithKeyAuth(url);
      let modifiedUrl = url;
      if (alteredUrlWithKeyAuth) {
        modifiedUrl = alteredUrlWithKeyAuth;
      }

      xhr.open('GET', modifiedUrl);
      if (this.authInterceptor) {
        this.authInterceptor.interceptXhr(xhr, modifiedUrl);
      }      

      xhr.onload = () => {
        if (xhr.status === 200 && xhr.responseText.length > 0) {
          const format = vectorSource.getFormat();
          const type = format.getType();

          let source: string | object | Document | Element | ArrayBuffer;
          let numberMatched = 0;

          try {
            if (type === 'xml') {
              const xml = new DOMParser().parseFromString(xhr.responseText, 'application/xml');
              source = xml;
              numberMatched = parseInt(xml.documentElement.getAttribute('numberMatched') || '0', 10);
            } else if (type === 'json') {
              const json = JSON.parse(xhr.responseText);
              source = json;
              numberMatched = json.numberMatched || json.totalFeatures || 0;
            } else {
              source = xhr.responseText;
            }
            if (source) {
              const features = format.readFeatures(source, {
                extent,
                featureProjection
              }) as olFeature[];

              vectorSource.addFeatures(features);
              resolve({ features, numberMatched });
            }
          } catch (err) {
            reject(err);
          }
        } else {
          reject();
        }
      };

      xhr.onerror = () => reject();
      xhr.send();
    });

  }


filterFeatures(features: olFeature[], selectionFeatures: olFeature[]): olFeature[] {
  const format = new GeoJSON();

  // Convert selection features to Turf geometries
  const selectionGeometries = selectionFeatures.map((f) =>
    turf.feature(format.writeFeatureObject(f).geometry)
  );

  // Filter features that intersect with any selection geometry
  const filteredFeatures = features.filter((feature) => {
    const featureGeoJSON = format.writeFeatureObject(feature);
    const featureGeometry = turf.feature(featureGeoJSON.geometry);

    return selectionGeometries.some((selGeom) =>
      turf.booleanIntersects(featureGeometry, selGeom)
    );
  });

  return filteredFeatures;
}


  createIgoLayer(title, features, sourceFields:SourceFieldsOptionsParams[]) {
    if (features.length === 0) {
      this.messageService.info(
        'sigt.integration.extractByPolygon.service.noFeaturesText',
        'sigt.integration.extractByPolygon.service.genericMessageTitle',
      );
      return;
    }

    const igoMap = this.#mapState.map;
    let layerTitle = this.checkExistingLayer(igoMap, title);
    const id = uuid();
    const sourceOptions:FeatureDataSourceOptions & QueryableDataSourceOptions  = {
        id,
        type: 'vector',
        queryable: true,
        sourceFields: sourceFields
    };
    const source = new FeatureDataSource(sourceOptions);

    const cleanedFeatures = this.sanitizeFeaturesAttributes(features, sourceFields);
    source.ol.addFeatures(cleanedFeatures);

    let randomStyle;
    let editable = false;
    randomStyle = featureRandomStyle();
    editable = true;

    const layer = this.#layerService.createLayer({
        id,
        title: layerTitle,
        workspace: { enabled: true, searchIndexEnabled: true },
        isIgoInternalLayer: true,
        source,
        igoStyle: { editable },
        style: randomStyle
    })  as VectorLayer;

    layer.setExtent(computeOlFeaturesExtent(cleanedFeatures, igoMap.viewProjection));
    igoMap.layerController.add(layer);
    moveToOlFeatures(igoMap.viewController, cleanedFeatures);
    return layer;
  }
  checkExistingLayer(igoMap:IgoMap, title:string):string {
    const existingLayers = igoMap.layerController.all.filter(l=>isLayerItem(l)&&l.title?.startsWith(title));
    
    if (existingLayers && existingLayers.length > 0) {
      return `${title} (${existingLayers.length})`;
    }

    return title;
  }

  async getArcgisRestFeatures(
    datasourceOptions: any,
    selectionFeature:olFeature<Geometry>
  ): Promise<olFeature<Geometry>[]>{

    const projection = getProjection(this.#mapState.map.projectionCode);
    
    const esriDataSourceOptions:any = {
      urlEsri: datasourceOptions.urlEsri,
      paramsEsri: datasourceOptions.paramsEsri,
      sourceFields: datasourceOptions.sourceFields
    };
    
    return new Promise<olFeature<Geometry>[]>((resolve, reject) => {  
      this.fetchArcgisRestFeaturesByChunks(
        esriDataSourceOptions, 
        selectionFeature,
        projection, 
        (features)=>{
        resolve(features);
        }, 
        ()=>{
        this.messageService.error(
          'sigt.integration.extractByPolygon.service.failedWFSExtract',
          'sigt.integration.extractByPolygon.service.genericMessageTitle',
        );
        reject(new Error('Failed to fetch WFS features'));;
      });
    });
  } 

  async fetchArcgisRestFeaturesByChunks(
    options: any,
    selectionFeature: olFeature,
    proj: olProjection,
    success: (features: olFeature[]) => void,
    failure: () => void,
    randomParam?: boolean
  ) {
    {
      const paramsEsri = options.paramsEsri;
      const baseUrl = `${options.urlEsri}/${paramsEsri.layer}/query`;
      const olFormat = new GeoJSON();
      const selFeature = JSON.parse(olFormat.writeFeature(selectionFeature));

      const featureRequest = Object.assign({},
        paramsEsri, this.getEsriGeomFilter(selFeature));
      //cleanup paramEsri
      delete featureRequest["layer"];
      delete featureRequest["queryFormat"];
      if (featureRequest.hasOwnProperty("maxFeatures")) {
        delete featureRequest["maxFeatures"];
      }
      if (featureRequest.hasOwnProperty("srsName")) {
        featureRequest["outSR"] = paramsEsri.srsName;
        delete featureRequest["srsName"];
      }
      featureRequest["outFields"] = "*";
      featureRequest["inSR"] = 102100; //the input geometry is in wkid:3857
      featureRequest["f"] = "json";
      featureRequest["outFields"]= "*";

      const esriProj = this.resoudreProjection(
        paramsEsri.srsName ? `EPSG:${paramsEsri.srsName}` : undefined,
        proj
      );

      let totalFeatures = Infinity;
      try {
        const paramsForCount = Object.assign({},
        featureRequest, {returnCountOnly: true});
        const {count} = await this.fetchArcgisRestFeaturesCount(baseUrl, paramsForCount);
         totalFeatures = count;
      } catch (error) {
        failure();
      } 

      const chunkSize = 1000;
      let startIndex = 0;
      let allFeatures: olFeature[] = [];

      try {
        while (startIndex < totalFeatures) {
          featureRequest["returnGeometry"] = true;
          featureRequest["resultOffset"] = startIndex;
          featureRequest["resultRecordCount"] = chunkSize;
          const { features, numberMatched } = await this.fetchArcgisRestFeaturesChunk(
            baseUrl,
            featureRequest
          );

          allFeatures = allFeatures.concat(features);
          startIndex += chunkSize;

          if (features.length === 0 || allFeatures.length >= totalFeatures) {
            break;
          }
        }

        success(allFeatures);
      } catch (error) {
        failure();
      }
    }
  }

  private async fetchArcgisRestFeaturesChunk(
    url: string,
    params:any
  ): Promise<{ features: olFeature[]; numberMatched: number }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Build URL-encoded string
      var body = Object.keys(params)
          .map(function(key) {
              var value = params[key];
              var formattedValue = typeof value === 'object' ? JSON.stringify(value) : value;
              return encodeURIComponent(key) + "=" + encodeURIComponent(formattedValue);
          })
          .join("&");

      const alteredUrlWithKeyAuth = this.authInterceptor.alterUrlWithKeyAuth(url);
      let modifiedUrl = url;
      if (alteredUrlWithKeyAuth) {
        modifiedUrl = alteredUrlWithKeyAuth;
      }

      xhr.open('POST', modifiedUrl, true);
      if (this.authInterceptor) {
        this.authInterceptor.interceptXhr(xhr, modifiedUrl);
      }
      xhr.setRequestHeader('Content-Type','application/x-www-form-urlencoded' );
      xhr.responseType = 'json';
      xhr.onload = () => {
        if (xhr.readyState === 4 && xhr.status === 200) {

          let source: string | object | Document | Element | ArrayBuffer;
          let numberMatched = 0;

          try {
              const queryResponse:ArcGISQueryResponse = xhr.response;
              if (queryResponse.error) {
                console.error(`ArcGIS Rest error: ${queryResponse.error.code} - ${queryResponse.error.message}`);
                reject(queryResponse.error.message);
              }
              numberMatched = queryResponse.numberReturned;
              const parser = new olFormatEsriJSON();
              const features = parser.readFeatures(queryResponse) as olFeature[];
              resolve({ features, numberMatched });
          } catch (err) {
            reject(err);
          }
        } else {
          reject();
        }
      };

      xhr.onerror = () => reject();
      xhr.send(body);
    });
  }  
  private async fetchArcgisRestFeaturesCount (  
    url: string,
    params:any
  ): Promise<{ count: number }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Build URL-encoded string
      var body = Object.keys(params)
          .map(function(key) {
              var value = params[key];
              var formattedValue = typeof value === 'object' ? JSON.stringify(value) : value;
              return encodeURIComponent(key) + "=" + encodeURIComponent(formattedValue);
          })
          .join("&");

      const alteredUrlWithKeyAuth = this.authInterceptor.alterUrlWithKeyAuth(url);
      let modifiedUrl = url;
      if (alteredUrlWithKeyAuth) {
        modifiedUrl = alteredUrlWithKeyAuth;
      }

      xhr.open('POST', modifiedUrl, true);
      if (this.authInterceptor) {
        this.authInterceptor.interceptXhr(xhr, modifiedUrl);
      }
      xhr.setRequestHeader('Content-Type','application/x-www-form-urlencoded' );
      xhr.responseType = 'json';
      xhr.onload = () => {
        if (xhr.readyState === 4 && xhr.status === 200) {

          let source: string | object | Document | Element | ArrayBuffer;
          let numberMatched = 0;

          try {
              const queryResponse:ArcGISQueryResponse = xhr.response;
              if (queryResponse.error) {
                console.error(`ArcGIS Rest error: ${queryResponse.error.code} - ${queryResponse.error.message}`);
                reject(queryResponse.error.message);
              }
              const count = queryResponse.count;
              resolve({ count });
          } catch (err) {
            reject(err);
          }
        } else {
          reject();
        }
      };

      xhr.onerror = () => reject();
      xhr.send(body);
    });
  } 

  getEsriGeomFilter(feature): any {
    const spatialReference = { wkid: 3857 };
    const geoJson = new GeoJSON();
    var geom: EsriGeometry;
    var esriFilter = {};
    switch (feature.type) {
      case 'Feature': {
        geom = this.convertToEsriGeometry((geoJson.readFeature(feature) as olFeature).getGeometry(), spatialReference);
        esriFilter = {
          geometryType: "esriGeometryPolygon",
          geometry: geom,
          spatialRel: "esriSpatialRelIntersects"
        };
        break;
      }
      case 'MultiPolygon':
      case 'Polygon': {
        geom = this.convertToEsriGeometry(geoJson.readGeometry(feature), spatialReference);
        esriFilter = {
          geometryType: "esriGeometryPolygon",
          geometry: geom,
          spatialRel: "esriSpatialRelIntersects"
        };
        break;
      }
      case 'Point':
        if (feature.radius) {
          geom = this.convertToEsriGeometry(geoJson.readGeometry(feature), spatialReference);
          esriFilter = {
            geometryType: "esriGeometryPoint",
            geometry: geom,
            distance: feature.radius,
            units: "esriSRUnit_Meter",
            spatialRel: "esriSpatialRelIntersects"
          };
          break;
        }
      default:
        throw ("Type de feature inconnu");
    }
    return esriFilter;
  }

  //utility method to convert OL geometry to esri geometry
  private convertToEsriGeometry(geometry: Geometry, spatialReference): EsriGeometry {
    if (!(geometry instanceof SimpleGeometry)) {
      throw new Error(`Unsupported geometry type: ${geometry.getType()}`);
    }
    const type = geometry.getType();
    let esriGeometry: EsriGeometry;

    switch (type) {
      case 'Point':
        esriGeometry = {
          x: geometry.getCoordinates()[0],
          y: geometry.getCoordinates()[1],
          spatialReference: spatialReference
        };
        break;
      case 'LineString':
        esriGeometry = {
          paths: [geometry.getCoordinates()],
          spatialReference: spatialReference
        };
        break;
      case 'Polygon':
        esriGeometry = {
          rings: geometry.getCoordinates(),
          spatialReference: spatialReference
        };
        break;
      case 'MultiPolygon':
        esriGeometry = {
          rings: geometry.getCoordinates().flat(),
          spatialReference: spatialReference
        };
        break;
      default:
        throw new Error(`Unsupported geometry type: ${type}`);
    }

    return esriGeometry;
  }
  private sanitizeFeaturesAttributes(
    features: olFeature[],
    sourceFields: SourceFieldsOptionsParams[]
  ): olFeature[] {
    
    if (!features?.length || !sourceFields?.length) {
      return features;
    }


    const orderedFieldNames = sourceFields.map(f => String(f.name));

    for (const feature of features) {
      const geometry = feature.getGeometry();
      const geometryName = feature.getGeometryName();
      const currentProps = feature.getProperties();

      // Remove all non-geometry properties first
      Object.keys(currentProps).forEach(key => {
        if (key !== geometryName) {
          feature.unset(key, true);
        }
      });

      // Re‑add properties in sourceFields order
      for (const fieldName of orderedFieldNames) {
        if (currentProps[fieldName] !== undefined) {
          feature.set(fieldName, currentProps[fieldName], true);
        }
      }

      // Re‑assign geometry (ensures it stays untouched)
      feature.setGeometry(geometry);
    }

    return features;
  }

}
